import axios from 'axios';
import { Auth0Client } from "@auth0/auth0-spa-js";
// import { useNavigate } from 'react-router-dom';

const AUTH0_DOMAIN = "dev-ql322tpo6jop3to2.us.auth0.com";  
const CLIENT_ID = "fHRrIFgn2CWHNoLAD3l7SE1m2cVTDNkS";
const AUDIENCE = "https://skyshiftETL";
const CONNECTION = "Username-Password-Authentication";
const BACKEND_URL = "https://hmetl.hyperminds.tech/auth";
// const BACKEND_URL = "http://localhost:8000/auth";
const USERINFO_ENDPOINT = `https://${AUTH0_DOMAIN}/userinfo`;

/**
 * ✅ Login with Auth0 (Username/Password)
 */
export const loginWithAuth0 = async (username, password) => {
  // const navigate = useNavigate();
  try {
    const auth0Response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
      grant_type: "http://auth0.com/oauth/grant-type/password-realm",
      client_id: CLIENT_ID,
      username,
      password,
      realm: CONNECTION,
      audience: AUDIENCE,
      scope: "openid profile email",
    });

    const token = auth0Response.data.access_token;

    // Call userinfo to get the user profile
    const userInfoResponse = await axios.get(USERINFO_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Call your backend to create/fetch user
    const backendResponse = await axios.get(`${BACKEND_URL}/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log("Backend Response:", backendResponse.data);
    // if (backendResponse.must_change_password === true || backendResponse.requires_password_change === true) {
    //   // Navigate to the password change page if must_change_password is true
    //   navigate('/change-password');
    //   return; // Return early since the user is being redirected
    // }

    return {
      token,
      user: userInfoResponse.data, // user profile info
      backendUser: backendResponse.data, // your DB user info
    };

  } catch (error) {
    // Relax email verification - allow login even if email verification fails
    const errorResponse = error.response?.data;
    const errorCode = errorResponse?.error || errorResponse?.code;
    const errorMessage = errorResponse?.error_description || errorResponse?.description || errorResponse?.message || '';
    
    // Check if error is related to email verification
    const isEmailVerificationError = 
      errorCode === 'email_not_verified' ||
      errorCode === 'email_verification_required' ||
      errorMessage?.toLowerCase().includes('email verification') ||
      errorMessage?.toLowerCase().includes('verify your email') ||
      errorMessage?.toLowerCase().includes('email not verified');
    
    // If it's an email verification error, log it but don't block login
    if (isEmailVerificationError) {
      console.warn("Email verification error detected but allowing login:", errorResponse);
      // Still throw the error - backend/Auth0 might still block, but we've logged it
    }
    
    console.error("Login Error:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Signup with Auth0 (Username/Password + Metadata)
 */
export const signupWithAuth0 = async (email, password, username, fullName, mobile, accountType, companyName) => {
  try {
    const trimmedFullName = fullName?.trim();
    const trimmedCompanyName = companyName?.trim();
    const normalizedAccountType = accountType === "business" ? "business" : "individual";
    const userMetadata = {
      ...(mobile ? { mobile } : {}),
      ...(normalizedAccountType ? { accountType: normalizedAccountType } : {}),
      ...(trimmedFullName ? { fullName: trimmedFullName } : {}),
      ...(normalizedAccountType === "business" && trimmedCompanyName ? { companyName: trimmedCompanyName } : {}),
    };

    // Step 1: Signup via Auth0
    const signupResponse = await axios.post(`https://${AUTH0_DOMAIN}/dbconnections/signup`, {
      client_id: CLIENT_ID,
      email,
      password,
      username,
      name: trimmedFullName || username || email,
      connection: CONNECTION,
      user_metadata: userMetadata,
    });

    console.log("Signup Successful:", signupResponse.data);

    // Step 2: Immediately login to get the token
    const authResponse = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
      grant_type: "password",
      client_id: CLIENT_ID,
      audience: AUDIENCE,
      username: email,  // Email is the username for Auth0 login
      password,
      scope: "openid profile email",
    });

    const token = authResponse.data.access_token;
    console.log("Token after signup:", token);

    // Step 3: Call userinfo to get the user profile
    const userInfoResponse = await axios.get(USERINFO_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Step 4: Call your backend to create/fetch user in DB
    try {
      const backendResponse = await axios.get(`${BACKEND_URL}/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Backend Response:", backendResponse.data);

      return {
        token,
        user: userInfoResponse.data,
        backendUser: backendResponse.data,
      };
    } catch (backendError) {
      if (backendError.response?.status === 403) {
        const errorDetail = backendError.response?.data?.detail;
        if (errorDetail?.requires_email_verification === true) {
          console.log("Signup successful, but email verification required");
          return {
            token,
            user: userInfoResponse.data,
            backendUser: null,
            requiresEmailVerification: true,
            message: "Account created successfully! Please verify your email address before logging in."
          };
        }
      }
      throw backendError;
    }

  } catch (error) {
    console.error("Signup Error:", error.response?.data || error.message);
    throw error;
  }
};

const auth0 = new Auth0Client({
  domain: AUTH0_DOMAIN,
  clientId: CLIENT_ID,
  authorizationParams: {
    audience: AUDIENCE,
    scope: "openid profile email",
    redirect_uri: "https://etl-frontend-zeta.vercel.app",
  },
  cacheLocation: "localstorage",
  useRefreshTokens: true,
});

/**
 * Login with Google (Popup)
 */
export const loginWithGoogle = async () => {
  try {
    // Step 1: Login with popup using Google
    await auth0.loginWithPopup({
      authorizationParams: {
        connection: "google-oauth2",
      },
    });

    // Step 2: Get Access Token from Auth0
    const token = await auth0.getTokenSilently();
    console.log("Google Login Token:", token);

    // Step 3: Call userinfo to get the user profile
    const userInfoResponse = await axios.get(USERINFO_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Step 4: Call your backend to create/fetch user in DB
    const backendResponse = await axios.get(`${BACKEND_URL}/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log("Backend Response:", backendResponse.data);

    return {
      token,
      user: userInfoResponse.data,
      backendUser: backendResponse.data,
    };

  } catch (error) {
    console.error("Google Login Failed:", error);
    throw error;
  }
};

// Forgot Password - Call Backend Instead of Auth0 Directly
export const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(
      "https://hmetl.hyperminds.tech/auth/forgot-password",
      new URLSearchParams({ email }).toString(), // Convert to form-encoded string
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );

    console.log("🔹 Backend Reset Password Response:", response.data);
    return response.data; // Return the reset URL
  } catch (error) {
    console.error("Password reset request failed:", error.response?.data || error.message);
    throw error;
  }
};
